import {
  Answer,
  Hint,
  Feedback,
  TypeArray,
  QuestionTarget,
  Other,
  Settings,
  TTS
} from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  getArrayTypeReducerGuard,
  processActionMeta,
  getObjectTypeReducerGuard
} from "../reducers"
import { Explanation } from "./Explanation"

export type Question = {
  answers: Array<Answer>
  explanation: Explanation
  feedbacks: Array<Feedback>
  hints: Array<Hint>
  question: string
  deltas: any
  tts: TTS
  other: Other
  settings: Settings
}

export const reducerAction = {
  answers: actionCreatorTypeGuard<Question, Array<Answer>>(
    "answers",
    "Question"
  ),
  explanation: actionCreatorTypeGuard<Question, Explanation>(
    "explanation",
    "Question"
  ),
  hints: actionCreatorTypeGuard<Question, Array<Hint>>("hints", "Question"),
  feedbacks: actionCreatorTypeGuard<Question, Array<Feedback>>(
    "feedbacks",
    "Question"
  ),
  question: actionCreatorTypeGuard<Question, string>("question", "Question"),
  deltas: actionCreatorTypeGuard<Question, any>("deltas", "Question"),
  tts: actionCreatorTypeGuard<Question, TTS>("tts", "Question"),
  other: actionCreatorTypeGuard<Question, Other>("other", "Question"),
  settings: actionCreatorTypeGuard<Question, Settings>("settings", "Question"),
  __record: actionCreatorTypeGuard<TypeArray, Question>("Question", "__record"),
  __create: actionCreatorTypeGuard<TypeArray, Partial<Question> | null>(
    "create",
    "__create/Question"
  ),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Question
    | "__record"
    | "__delete"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const questionAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const questionReducer = reducerTypeGuard<Question>()
  .case(...reducerAction.answers)
  .case(...reducerAction.explanation)
  .case(...reducerAction.hints)
  .case(...reducerAction.feedbacks)
  .case(...reducerAction.question)
  .case(...reducerAction.deltas)
  .case(...reducerAction.tts)
  .case(...reducerAction.settings)
  .case(...reducerAction.other)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "answers":
        return getArrayTypeReducerGuard<Question, Answer>(
          "questions",
          "answers",
          "Answer",
          prevState,
          action
        )
      case "explanation":
        return getObjectTypeReducerGuard<Question, Explanation>(
          "questions",
          "explanation",
          "Explanation",
          prevState,
          action
        )
      case "feedbacks":
        return getArrayTypeReducerGuard<Question, Feedback>(
          "questions",
          "feedbacks",
          "Feedback",
          prevState,
          action
        )
      case "hints":
        return getArrayTypeReducerGuard<Question, Hint>(
          "questions",
          "hints",
          "Hint",
          prevState,
          action
        )
      case "other":
        return getObjectTypeReducerGuard<Question, Other>(
          "questions",
          "other",
          "Other",
          prevState,
          action
        )
      case "settings":
        return getObjectTypeReducerGuard<Question, Settings>(
          "questions",
          "settings",
          "Settings",
          prevState,
          action
        )
    }

    return prevState
  })
